(function () {
  'use strict';

  function badgePercentage(result, variant, rendered) {
    if (!result || result.isCurrentProduct !== true || result.isDiscountedByOffer !== true
      || result.sellingPlan != null || !result.offerToken
      || String(result.variantId) !== String(variant.id) || result.productHandle !== variant.handle
      || !Number.isSafeInteger(variant.price) || variant.price <= 0 || variant.compareAt > variant.price
      || result.originalPrice !== variant.price
      || !Number.isSafeInteger(result.discountedPrice) || result.discountedPrice < 0
      || result.discountedPrice >= variant.price
      || !Number.isFinite(result.percentage) || result.percentage <= 0 || result.percentage > 100
      || rendered.price !== result.discountedPrice || rendered.original !== variant.price
      || rendered.offerToken !== result.offerToken) return null;
    return Math.floor(Math.min(result.percentage, (variant.price - result.discountedPrice) * 100 / variant.price));
  }

  const mounted = new WeakMap();
  function mount(doc) {
    if (mounted.has(doc)) return mounted.get(doc);
    const view = doc.defaultView;
    const entries = view.CgDiscountBenefit?.campaignEntries(doc) || [];
    const selector = '.cg-dn-price[data-cg-dn-variant]';
    let confirmations = new WeakMap();
    let pendingVariant = null;
    const results = new Map();
    const reserved = new Map();
    let frame = 0;
    let stopped = false;

    function reconcile() {
      frame = 0;
      if (stopped) return;
      let consumed = false;
      doc.querySelectorAll(selector).forEach(element => {
        if (pendingVariant && String(pendingVariant.variantId) === element.dataset.cgDnVariant
          && pendingVariant.productHandle === element.dataset.cgDnHandle) {
          confirmations.set(element, pendingVariant);
          consumed = true;
        }
        const replacement = element.querySelector('[data-la-dn-replaced-root]');
        const price = replacement?.querySelector('[data-la-dn-shortcode-product="DISCOUNTED_PRICE"]');
        const original = replacement?.querySelector('[data-la-dn-shortcode-product="ORIGINAL_PRICE,STRIKETHROUGH"]');
        const cents = node => Number(node?.querySelector('[data-limoniapps-discountninja-price-cents-presentmentcurrency]')
          ?.getAttribute('data-limoniapps-discountninja-price-cents-presentmentcurrency'));
        const percentage = price && original ? badgePercentage(confirmations.get(element), {
          id: element.dataset.cgDnVariant,
          handle: element.dataset.cgDnHandle,
          price: Number(element.dataset.cgDnPrice),
          compareAt: Number(element.dataset.cgDnCompareAt)
        }, { price: cents(price), original: cents(original), offerToken: replacement.dataset.token }) : null;
        const active = percentage !== null;
        element.classList.toggle('cg-dn-price--active', active);
        let benefit = element.querySelector('[data-cg-pdp-benefit]');
        if (!active) { benefit?.remove(); return; }
        if (!benefit) {
          benefit = doc.createElement('span');
          benefit.dataset.cgPdpBenefit = '';
          element.append(benefit);
        }
        const result = confirmations.get(element);
        view.CgDiscountBenefit?.render(benefit, {
          percentage, saving: result.originalPrice - result.discountedPrice,
          label: view.CgDiscountBenefit.pdpLabel(result, view.discountNinja?.api?.entitlements || [], entries)
        });
        // Xotiny replaces the inner price while this wrapper survives. Reserve
        // only its confirmed layout, never an old variant's price or badge.
        const wrapper = element.closest('xo-product-will-change');
        if (wrapper) {
          const width = wrapper.getBoundingClientRect().width;
          const previous = reserved.get(wrapper);
          const original = previous?.original ?? wrapper.style.minBlockSize;
          const height = Math.max(previous?.width === width ? previous.height : 0, element.getBoundingClientRect().height);
          if (width && height) {
            reserved.set(wrapper, {original, width, height});
            wrapper.style.minBlockSize = height + 'px';
          }
        }
      });
      if (consumed) pendingVariant = null;
    }
    function schedule() {
      if (!stopped && !frame) frame = view.requestAnimationFrame(reconcile);
    }
    function calculated(event) {
      const next = event.detail?.data?.[0];
      if (!next?.isCurrentProduct) return;
      results.set(String(next.variantId), next);
      // Confirm only the currently mounted Liquid roots, never a future replacement.
      confirmations = new WeakMap();
      const roots = [...doc.querySelectorAll(selector)];
      roots.forEach(element => confirmations.set(element, next));
      // Xotiny can commit the variant HTML after the app's fresh result arrives.
      // Consume that result once; never reuse it for a later same-variant root.
      pendingVariant = roots.length && !roots.some(element =>
        element.dataset.cgDnVariant === String(next.variantId)
        && element.dataset.cgDnHandle === next.productHandle) ? next : null;
      schedule();
    }
    function restored(event) {
      if (!event.persisted) return;
      confirmations = new WeakMap();
      pendingVariant = null;
      results.clear();
      schedule();
      // A restored app price can outlive its offer. Reload once to let the app
      // confirm both the price and badge; a normal load is not persisted.
      if (doc.querySelector(selector + ' [data-la-dn-replaced-root]')) view.location.reload();
    }
    function registerRenderer() {
      const pricing = view.discountNinja?.api?.strikethroughPricing;
      if (!pricing?.setBeforeRender) return;
      pricing.setBeforeRender(input => {
        const info = input.productInfo;
        const result = results.get(String(info?.variantId));
        // A fresh native render can follow a theme root replacement without a new
        // calculation event. Confirm it only against matching public price data.
        if (!stopped && result && result.offerToken === input.offerToken
          && result.productHandle === info.productHandle
          && result.originalPrice === info.price.original
          && result.discountedPrice === info.price.discounted
          && (result.sellingPlan ?? null) === (info.sellingPlanId ?? null)) {
          doc.querySelectorAll(selector).forEach(element => {
            if (element.dataset.cgDnVariant === String(info.variantId)
              && element.dataset.cgDnHandle === info.productHandle) confirmations.set(element, result);
          });
          schedule();
        }
        return input.defaultHtml;
      });
    }
    const observer = new view.MutationObserver(schedule);
    observer.observe(doc.documentElement, { childList: true, subtree: true });
    doc.addEventListener('la:dn:product:discount:calculated', calculated);
    doc.addEventListener('shopify:section:load', schedule);
    doc.addEventListener('la:dn:entitlements:calculated', schedule);
    doc.addEventListener('la:dn:api:ready', registerRenderer);
    view.addEventListener('pageshow', restored);
    view.addEventListener('resize', schedule);
    function cleanup() {
      if (stopped) return;
      stopped = true;
      observer.disconnect();
      view.cancelAnimationFrame(frame);
      doc.removeEventListener('la:dn:product:discount:calculated', calculated);
      doc.removeEventListener('shopify:section:load', schedule);
      doc.removeEventListener('la:dn:entitlements:calculated', schedule);
      doc.removeEventListener('la:dn:api:ready', registerRenderer);
      view.removeEventListener('pageshow', restored);
      view.removeEventListener('resize', schedule);
      reserved.forEach((value, wrapper) => { wrapper.style.minBlockSize = value.original; });
      reserved.clear();
      doc.querySelectorAll(selector).forEach(element => {
        element.classList.remove('cg-dn-price--active');
        element.querySelector('[data-cg-pdp-benefit]')?.remove();
      });
      mounted.delete(doc);
    }
    mounted.set(doc, cleanup);
    registerRenderer();
    schedule();
    return cleanup;
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { badgePercentage, mount };
    return;
  }
  if (window.CgDiscountNinja) return;
  window.CgDiscountNinja = { mount };
  mount(document);
})();
